import React, { useState, useEffect, useRef } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    X,
    Copy,
    MoreHorizontal,
    SquareArrowOutUpRight,
} from 'lucide-react';
import { useHistory } from 'react-router-dom';
import apiHelper from '../apiHelper';
import noImage from '../assets/img/brand/noImage.png';
import noBrandImage from '../assets/img/brand/no-brand-img.png';
import Pagination from './Pagination';
import { formatDateTime } from '../utils/formateDate';

const SKU_VARIATIONS_PER_PAGE = 5;
const SKU_CONFIGS_PER_PAGE    = 5;

const SpuInfoDrawer = ({ spuId, onBack, onClose, onBrandClick }) => {
    const history = useHistory();
    const toolbarRef = useRef(null);

    const [spu,                  setSpu]                  = useState(null);
    const [loading,              setLoading]              = useState(true);
    const [drawerTab,            setDrawerTab]            = useState('general');
    const [copied,               setCopied]               = useState(false);
    const [toolbarDropdownOpen,  setToolbarDropdownOpen]  = useState(false);
    const [imageModalOpen,       setImageModalOpen]       = useState(false);
    const [previewImages,        setPreviewImages]        = useState([]);
    const [previewIndex,         setPreviewIndex]         = useState(0);
    const [skuVariations,        setSkuVariations]        = useState([]);
    const [skuVariationPage,     setSkuVariationPage]     = useState(1);
    const [skuConfigurations,    setSkuConfigurations]    = useState([]);
    const [skuConfigPage,        setSkuConfigPage]        = useState(1);
    const [skuSetupLoaded,       setSkuSetupLoaded]       = useState(false);
    const [categoryDisplay,      setCategoryDisplay]      = useState(null);
    const [brandLogo,            setBrandLogo]            = useState(null);

    const toggleToolbarDropdown = () => setToolbarDropdownOpen(prev => !prev);

    // ── Fetch SPU full data ──
    useEffect(() => {
        if (!spuId) return;
        setLoading(true);
        setSpu(null);
        setCategoryDisplay(null);
        setBrandLogo(null);
        setDrawerTab('general');
        setSkuVariations([]);
        setSkuConfigurations([]);
        setSkuSetupLoaded(false);
        setToolbarDropdownOpen(false);

        apiHelper(`inventory/spu-sku/full/${spuId}/`, 'GET')
            .then(res => (res && res.ok ? res.json() : null))
            .then(data => {
                setSpu(data);
                setLoading(false);

                // Build category hierarchy
                if (data && Array.isArray(data.category_hierarchy) && data.category_hierarchy.length > 0) {
                    const parts = data.category_hierarchy.map(c => c.name).filter(Boolean);
                    setCategoryDisplay(parts.join(' >> ') || null);
                } else if (data && data.category_id) {
                    apiHelper(`inventory/categories/show/${data.category_id}/`, 'GET')
                        .then(res => (res && res.ok ? res.json() : null))
                        .then(cat => {
                            if (cat) {
                                const parts = [cat.parent_name, cat.category_name].filter(Boolean);
                                setCategoryDisplay(parts.length > 0 ? parts.join(' >> ') : cat.category_name || null);
                            }
                        })
                        .catch(() => {});
                }

                // Fetch brand logo separately if not included in the full/ endpoint response
                if (data && data.brand_id && !data.brand_logo) {
                    apiHelper(`inventory/brands/show/${data.brand_id}/`, 'GET')
                        .then(res => (res && res.ok ? res.json() : null))
                        .then(brand => { if (brand && brand.original_logo_url) setBrandLogo(brand.original_logo_url); })
                        .catch(() => {});
                }
            })
            .catch(() => setLoading(false));
    }, [spuId]);

    // ── Fetch SKU setup lazily when tab is first opened ──
    useEffect(() => {
        if (drawerTab !== 'sku-setup' || skuSetupLoaded || !spuId) return;

        apiHelper(`inventory/spu-sku/spu/${spuId}/sku-setup/`, 'GET')
            .then(res => (res && res.ok ? res.json() : null))
            .then(data => {
                if (!data) { setSkuSetupLoaded(true); return; }
                setSkuVariations(data.variations.map(v => ({
                    name: v.preset_name,
                    options: v.options.map(opt => ({ id: opt.option_id, label: opt.option_name })),
                })));
                setSkuConfigurations(data.configurations.map(cfg => ({
                    configuration_id:   cfg.configuration_id,
                    configuration_name: cfg.configuration_name,
                    skus: Array.isArray(cfg.skus) ? cfg.skus : [],
                })));
                setSkuSetupLoaded(true);
            })
            .catch(() => setSkuSetupLoaded(true));
    }, [drawerTab, skuSetupLoaded, spuId]);

    // ── Helpers ──
    const isBrandNameOverflowing = (name) => (name ? name.length > 30 : false);

    const handleCopy = async (textToCopy) => {
        if (!textToCopy) return;
        if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(textToCopy);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch (err) {
                fallbackCopy(textToCopy);
            }
        } else {
            fallbackCopy(textToCopy);
        }
    };

    const fallbackCopy = (text) => {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '0';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // ── SKU Variations pagination ──
    const totalSkuVariations      = skuVariations.length;
    const startIndex              = (skuVariationPage - 1) * SKU_VARIATIONS_PER_PAGE;
    const endIndex                = Math.min(startIndex + SKU_VARIATIONS_PER_PAGE, totalSkuVariations);
    const paginatedSkuVariations  = skuVariations.slice(startIndex, endIndex);
    const totalSkuVariationsPages = Math.ceil(totalSkuVariations / SKU_VARIATIONS_PER_PAGE);

    // ── SKU Configurations pagination ──
    const totalSkuConfigs            = skuConfigurations.length;
    const totalSkuConfigPages        = Math.ceil(totalSkuConfigs / SKU_CONFIGS_PER_PAGE);
    const configStartIndex           = (skuConfigPage - 1) * SKU_CONFIGS_PER_PAGE;
    const configEndIndex             = Math.min(configStartIndex + SKU_CONFIGS_PER_PAGE, totalSkuConfigs);
    const paginatedSkuConfigurations = skuConfigurations.slice(configStartIndex, configEndIndex);

    return (
        <>
            <div className="drawer-overlay" />
            <div className="entity-drawer">

                {/* ── Header ── */}
                <div className="drawer-header" style={{ background: '#F8F9FA' }}>
                    <div className="d-flex align-items-center gap-1">
                        <button
                            className="back-btn"
                            onClick={onBack}
                            style={{ backgroundColor: 'transparent', border: 'none', padding: 0, margin: 0 }}
                        >
                            <ChevronLeft size={24} />
                        </button>
                        <h3>SPU Info</h3>
                    </div>
                    <button className="close-btn" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="main-drawer-inner" style={{ marginTop: '62px' }}>

                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6C757D', fontFamily: 'Montserrat', fontSize: '14px' }}>
                            Loading...
                        </div>
                    ) : !spu ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6C757D', fontFamily: 'Montserrat', fontSize: '14px' }}>
                            Failed to load SPU info.
                        </div>
                    ) : (
                        <>
                            {/* ── Drawer Title + Toolbar ── */}
                            <div className="drawer-title d-flex align-items-center justify-content-between"
                                style={{
                                    borderBottom: 'none',
                                    paddingBottom: '0px',
                                    boxShadow: 'none',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'nowrap',
                                    width: '100%',
                                }}>
                                <div className='d-flex align-items-center'>
                                    <img
                                        src={
                                            spu?.images && spu?.images.length > 0
                                                ? spu.images[0].spu_image_url || spu.images[0].spu_image_name || noImage
                                                : noImage
                                        }
                                        alt="avatar"
                                        onError={(e) => { e.target.onerror = null; e.target.src = noImage; }}
                                        style={{ width: 40, height: 40, marginRight: 8 }}
                                    />
                                    <h2 style={{ textTransform: 'none' }} className='pb-0'>{spu?.model_name}</h2>
                                </div>
                               
                            </div>

                            {/* ── Tabs ── */}
                            <div className="drawer-tabs" style={{ borderBottom: 'none', gap: '20px', paddingBottom: '15px' }}>
                                <button className={drawerTab === 'general' ? 'active' : ''} onClick={() => setDrawerTab('general')}>
                                    General
                                </button>
                                <button className={drawerTab === 'additional-info' ? 'active' : ''} onClick={() => setDrawerTab('additional-info')}>
                                    Additional
                                </button>
                                <button className={drawerTab === 'sku-setup' ? 'active' : ''} onClick={() => setDrawerTab('sku-setup')}>
                                    SKU Setup
                                </button>
                            </div>

                            {/* ── Tab Content ── */}
                            <div className="drawer-content">

                                {/* ══════════ GENERAL ══════════ */}
                                {drawerTab === 'general' && (
                                    <div className="tab-panel general-panel">

                                        {/* Basic Info */}
                                        <div className="card-section" style={{ borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden', marginBottom: '20px', marginTop: '15px' }}>
                                            <div className="card-header" style={{ backgroundColor: ' #F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: ' #212529', display: 'flex', alignItems: 'center', alignSelf: 'stretch', borderRadius: '12px 12px 0px 0px', gap: '10px', height: '50px', borderBottom: 'none' }}>
                                                Basic Info
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#FFFFFF', padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>

                                                <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column',  gridColumn: isBrandNameOverflowing(spu.model_name) ? '1 / -1' : '1', gridRow: '1', padding: '0' }}>
                                                    <div className="field-label">SPU Name</div>
                                                    <div className="field-value" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {spu.model_name || <span style={{ color: '#B0B0B0' }}>NA</span>}
                                                    </div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column',  gridColumn: isBrandNameOverflowing(spu.model_name) ? '1' : '2', gridRow: isBrandNameOverflowing(spu.model_name) ? '2' : '1', padding: '0' }}>
                                                    <div className="field-label">Prefix</div>
                                                    <div className="field-value" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {spu.prefix || <span style={{ color: '#B0B0B0' }}>NA</span>}
                                                    </div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column',  padding: '0' }}>
                                                    <div className="field-label">Status</div>
                                                    <div className="field-value">
                                                        <span className={`status ${String(spu?.status || '').toLowerCase()}`}>
                                                            {spu?.status || <span style={{ color: '#B0B0B0' }}>NA</span>}
                                                        </span>
                                                    </div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', padding: '0' }}>
                                                    <div className="field-label" style={{ marginBottom: '4px' }}>ID</div>
                                                    <div className="field-value" style={{ width: '100%', wordBreak: 'break-all' }}>
                                                        {spu.model_id || <span style={{ color: '#B0B0B0' }}>NA</span>}
                                                    </div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <div className="field-label" style={{ marginBottom: '4px' }}>Open ID</div>
                                                    <div className="field-value cell-wrapper">
                                                        {spu.uuid ? (
                                                            <div className="value-container">
                                                                <span style={{ cursor: 'pointer' }} className="value-clickable" onClick={() => handleCopy(spu.uuid)}>
                                                                    {spu.uuid}
                                                                </span>
                                                                <Copy size={13} className="copy-icon" onClick={() => handleCopy(spu.uuid)} />
                                                                {copied && <span className="copied-label">Copied!</span>}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#B0B0B0' }}>NA</span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0' }}>
                                                    <div className="field-label">Created By</div>
                                                    <div className="field-value">{spu.created_by || <span style={{ color: '#B0B0B0' }}>NA</span>}</div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0' }}>
                                                    <div className="field-label">Created Time</div>
                                                    <div className="field-value">{formatDateTime(spu.created_at) || <span style={{ color: '#B0B0B0' }}>NA</span>}</div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0' }}>
                                                    <div className="field-label">Last Modified By</div>
                                                    <div className="field-value">{spu.modified_by || <span style={{ color: '#B0B0B0' }}>NA</span>}</div>
                                                </div>

                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0' }}>
                                                    <div className="field-label">Last Modified Time</div>
                                                    <div className="field-value">{formatDateTime(spu.modified_at) || <span style={{ color: '#B0B0B0' }}>NA</span>}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Product Info */}
                                        <div className="card-section" style={{ borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden' }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '10px 16px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#212529', borderBottom: 'none' }}>
                                                Product Info
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#FFFFFF', padding: '16px', display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) 1fr', gap: '16px', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>

                                                {/* Brand */}
                                                <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column',  gridColumn: '1 / -1', alignItems: 'flex-start', width: '100%', padding: '0' }}>
                                                    <div className="field-label">Brand</div>
                                                    {/* OLD brand display: logo + redirect link — commented out
                                                    <div className="field-value">
                                                        {spu.brand_name ? (
                                                            <div className="d-flex align-items-center">
                                                                <img src={spu?.brand_logo || brandLogo || noImage} alt="parent brand logo" className="avatar"
                                                                    onError={(e) => { e.target.onerror = null; e.target.src = noImage; }}
                                                                    style={{ width: 32, height: 32, marginRight: 10, flexShrink: 0, verticalAlign: 'middle' }} />
                                                                <div className="me-3" style={{ color: '#2C7BE5', textDecoration: 'underline', fontWeight: 600, fontSize: '12px', cursor: 'pointer', lineHeight: '1.4', maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere', whiteSpace: 'normal' }}
                                                                    onClick={(e) => { e.stopPropagation(); if (spu.brand_id) { if (onBrandClick) { onBrandClick(spu.brand_id); } else { history.push(`/brand?id=${spu.brand_id}&autoOpen=true`); } } }}>
                                                                    <span style={{ display: 'inline', wordBreak: 'break-word' }}>
                                                                        {spu.brand_name}
                                                                        <SquareArrowOutUpRight size={14} strokeWidth={3} style={{ marginLeft: '4px', verticalAlign: 'text-top', color: '#2C7BE5' }}
                                                                            onClick={(e) => { e.stopPropagation(); if (spu.brand_id) { if (onBrandClick) { onBrandClick(spu.brand_id); } else { history.push(`/brand?id=${spu.brand_id}&autoOpen=true`); } } }} />
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ) : (<span style={{ color: '#B0B0B0' }}>NA</span>)}
                                                    </div>
                                                    END old brand display */}
                                                    <div className="field-value" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {spu.brand_hierarchy && spu.brand_hierarchy.length > 0
                                                            ? spu.brand_hierarchy.map(b => b.name).join(' >> ')
                                                            : spu.brand_name
                                                                ? spu.brand_name
                                                                : <span style={{ color: '#B0B0B0' }}>NA</span>
                                                        }
                                                    </div>
                                                </div>

                                                {/* Category — always spans both columns so long paths stay on one line */}
                                                <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', gridColumn: '1 / -1', alignItems: 'flex-start', width: '100%', padding: '0' }}>
                                                    <div className="field-label">Category</div>
                                                    <div className="field-value" style={{ wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                        {categoryDisplay
                                                            ? categoryDisplay
                                                            : spu.category_name
                                                                ? spu.category_name
                                                                : <span style={{ color: '#B0B0B0' }}>NA</span>
                                                        }
                                                    </div>
                                                </div>

                                                {/* Type */}
                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                    <div className="field-label">Type</div>
                                                    <div className="field-value">
                                                        {spu.product_type ? (
                                                            <div style={{ display: 'flex', height: '20px', minWidth: '72px', padding: '5px 10px', justifyContent: 'center', alignItems: 'center', gap: '5px', borderRadius: '99px', border: '1px solid #FFEB00', background: '#FFEB00', fontSize: '12px', fontWeight: 600, color: '#212529', width: 'fit-content' }}>
                                                                {spu?.product_type}
                                                            </div>
                                                        ) : (
                                                            <span style={{ color: '#B0B0B0' }}>NA</span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Release Date */}
                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                    <div className="field-label">Release Date</div>
                                                    <div className="field-value">{spu.release_date || <span style={{ color: '#B0B0B0' }}>NA</span>}</div>
                                                </div>

                                                {/* Images */}
                                                <div className="field-block" style={{ borderBottom: 'none', padding: '0', gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                                                    <div className="field-label" style={{ marginBottom: '4px' }}>Images</div>
                                                    {(() => {
                                                        const hasImages = Array.isArray(spu.images) && spu.images.length > 0;
                                                        return (
                                                            <div className="field-value" style={{ width: '100%', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                                {!hasImages && (
                                                                    <img
                                                                        src={noImage}
                                                                        alt="no image"
                                                                        style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, opacity: 0.7 }}
                                                                        onClick={() => { setPreviewImages([noImage]); setPreviewIndex(0); setImageModalOpen(true); }}
                                                                    />
                                                                )}
                                                                {hasImages && spu?.images?.map((imgObj, index) => {
                                                                    const imageUrl = imgObj.spu_image_url || imgObj.spu_image_name;
                                                                    return (
                                                                        <img
                                                                            key={imgObj.spu_image_id}
                                                                            src={imageUrl}
                                                                            alt="spu"
                                                                            style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                                                                            onClick={() => {
                                                                                const previewList = spu?.images?.map(i => i.spu_image_url || i.spu_image_name);
                                                                                setPreviewImages(previewList);
                                                                                setPreviewIndex(index);
                                                                                setImageModalOpen(true);
                                                                            }}
                                                                            onError={(e) => (e.target.src = noImage)}
                                                                        />
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Image Preview Modal — same as spusku.js */}
                                        {imageModalOpen && (
                                            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(3px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 5000 }}>
                                                <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', maxWidth: '650px', maxHeight: '80vh', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    <img
                                                        src={previewImages[previewIndex]}
                                                        alt="preview"
                                                        style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '12px' }}
                                                    />
                                                    <button
                                                        onClick={() => setImageModalOpen(false)}
                                                        style={{ position: 'fixed', top: '0px', right: '0px', background: 'white', width: '40px', height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.2)', cursor: 'pointer', zIndex: 6000 }}
                                                    >
                                                        <X size={22} />
                                                    </button>
                                                </div>
                                                {previewImages.length > 1 && (
                                                    <button
                                                        onClick={() => setPreviewIndex(i => (i - 1 + previewImages.length) % previewImages.length)}
                                                        style={{ position: 'fixed', left: '0px', top: '50%', transform: 'translateY(-50%)', background: 'white', width: '40px', height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', cursor: 'pointer' }}
                                                    >
                                                        <ChevronLeft size={28} />
                                                    </button>
                                                )}
                                                {previewImages.length > 1 && (
                                                    <button
                                                        onClick={() => setPreviewIndex(i => (i + 1) % previewImages.length)}
                                                        style={{ position: 'fixed', right: '0px', top: '50%', transform: 'translateY(-50%)', background: 'white', width: '40px', height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', cursor: 'pointer' }}
                                                    >
                                                        <ChevronRight size={28} />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ══════════ ADDITIONAL ══════════ */}
                                {drawerTab === 'additional-info' && (
                                    <div className="tab-panel general-panel">
                                        <div className="card-section" style={{ borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden', marginTop: '15px' }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#212529', borderBottom: 'none', height: '50px' }}>
                                                Customised Info
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#FFFFFF', padding: '16px', display: 'grid', gridTemplateColumns: 'minmax(150px, 1fr) 1fr', gap: '16px', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                {spu?.custom_fields?.length > 0 ? (
                                                    spu.custom_fields.map((field) => (
                                                        <div key={field.field_id} className="field-block" style={{ borderBottom: 'none', padding: '0', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                            <div className="field-label">{field.field_name || 'NA'}</div>
                                                            <div className="field-value">{field.field_data || <span style={{ color: '#B0B0B0' }}>NA</span>}</div>
                                                        </div>
                                                    ))
                                                ) : (
                                                    <div style={{ color: '#ADB5BD' }}>No custom fields available</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="card-section" style={{ borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden', marginTop: '40px' }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#212529', borderBottom: 'none', height: '50px' }}>
                                                Notes
                                            </div>
                                            <div className="card-body" style={{ backgroundColor: '#FFFFFF', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal' }}>
                                                <div className="field-block" style={{ padding: '0' }}>
                                                    <div className="field-value notes-preserve" style={{ background: '#FFFFFF', padding: '0', fontSize: '13px', color: spu.notes ? '#212529' : '#ADB5BD', minHeight: '24px' }}>
                                                        {spu.notes || 'NA'}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ══════════ SKU SETUP ══════════ */}
                                {drawerTab === 'sku-setup' && (
                                    <div style={{ padding: '10px 0', display: 'flex', flexDirection: 'column', gap: '40px' }}>

                                        {/* SKU VARIATIONS */}
                                        <div className="card-section" style={{ borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden' }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#212529', borderBottom: 'none' }}>
                                                SKU Variations
                                            </div>
                                            <div style={{ display: 'grid', gridTemplateColumns: '102px 1fr', background: '#E9ECEF', padding: '10px', fontWeight: 700, fontSize: '14px', margin: '10px', height: '45px', gap: '12px', alignItems: 'center', color: '#212529' }}>
                                                <div style={{ color: '#212529', textAlign: 'center', fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal', fontWeight: 700, lineHeight: 'normal' }} className='text-center'>SKU Variation</div>
                                                <div style={{ color: '#212529', textAlign: 'center', fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal', fontWeight: 700, lineHeight: 'normal' }} className='text-center'>Variation Options</div>
                                            </div>
                                            {!skuSetupLoaded ? (
                                                <div style={{ padding: '20px', textAlign: 'center', color: '#6C757D' }}>Loading...</div>
                                            ) : Array.isArray(skuVariations) && skuVariations.length > 0 ? (
                                                paginatedSkuVariations.map((item, index) => {
                                                    const rowBgColor = index % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
                                                    return (
                                                        <div key={index} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', borderBottom: '1px solid #E9ECEF', gap: '12px', backgroundColor: rowBgColor, marginLeft: '10px', marginRight: '10px' }}>
                                                            <div className='text-center' style={{ fontSize: '12px', fontWeight: 400, padding: '10px', color: '#212529', paddingLeft: '20px' }}>
                                                                {item?.name || '—'}
                                                            </div>
                                                            <div className='text-center' style={{ display: 'flex', flexWrap: 'wrap', padding: '5px 10px', gap: '10px', color: '#212529' }}>
                                                                {Array.isArray(item?.options) && item.options.length > 0 ? (
                                                                    item.options.map((opt, idx) => (
                                                                        <span key={idx} style={{ background: '#FFEB3B', padding: '5px 10px', borderRadius: '99px', fontWeight: 600, fontSize: '12px', display: 'inline-block' }}>
                                                                            {opt.label || 'Unknown'}
                                                                        </span>
                                                                    ))
                                                                ) : (
                                                                    <span style={{ fontSize: '12px', color: '#ADB5BD' }}>NA</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div style={{ padding: '20px', textAlign: 'center', color: '#6C757D' }}>No SKU Variations Found</div>
                                            )}
                                            {totalSkuVariations > 0 && (
                                                <div className="pagination" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <div className="pagination-info" style={{ textAlign: 'right', paddingTop: '10px', fontSize: '12px', color: '#6C757D', paddingBottom: '10px', paddingRight: '10px' }}>
                                                        {`${startIndex + 1} - ${endIndex} of ${totalSkuVariations}`}
                                                    </div>
                                                    <Pagination currentPage={skuVariationPage} totalPages={totalSkuVariationsPages} onPageChange={setSkuVariationPage} />
                                                </div>
                                            )}
                                        </div>

                                        {/* SKU CONFIGURATIONS */}
                                        <div className="card-section" style={{ borderRadius: '12px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'hidden' }}>
                                            <div className="card-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: '#212529', borderBottom: 'none' }}>
                                                SKU Configurations
                                            </div>
                                            {totalSkuConfigs > 0 && (
                                                <>
                                                    <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', background: '#E9ECEF', padding: '10px', fontWeight: 700, fontSize: '14px', margin: '10px', height: '45px', gap: '12px', alignItems: 'center', color: '#212529' }}>
                                                        <div style={{ color: '#212529', textAlign: 'center', fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal', fontWeight: 700, lineHeight: 'normal' }} className='text-center'>Configuration Name</div>
                                                        <div style={{ color: '#212529', textAlign: 'center', fontFamily: 'Montserrat', fontSize: '12px', fontStyle: 'normal', fontWeight: 700, lineHeight: 'normal' }} className='text-center'>Applied SKUs</div>
                                                    </div>
                                                </>
                                            )}
                                            {!skuSetupLoaded ? (
                                                <div style={{ padding: '20px', textAlign: 'center', color: '#6C757D' }}>Loading...</div>
                                            ) : Array.isArray(paginatedSkuConfigurations) && paginatedSkuConfigurations.length > 0 ? (
                                                paginatedSkuConfigurations.map((cfg, index) => {
                                                    const rowBgColor = index % 2 === 0 ? '#FFFFFF' : '#F8F9FA';
                                                    const hasSkus = Array.isArray(cfg?.skus) && cfg.skus.length > 0;
                                                    return (
                                                        <div key={index} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', padding: '10px 0px', paddingLeft: '20px', borderBottom: '1px solid #E9ECEF', gap: '12px', margin: '0px 12px', backgroundColor: rowBgColor }}>
                                                            <div className="text-center" style={{ fontSize: '12px', fontWeight: 400, color: '#212529', wordBreak: 'break-word', overflowWrap: 'auto' }}>
                                                                {cfg.configuration_name || <span style={{ color: '#ADB5BD' }}>Unknown Configuration</span>}
                                                            </div>
                                                            <div className="text-center" style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', lineHeight: '20px', fontSize: '12px', color: '#212529', padding: '0px 10px' }}>
                                                                {hasSkus ? (
                                                                    cfg.skus.map((sku, i) => (
                                                                        <span key={i} style={{ fontSize: '12px', margin: '2px' }}>
                                                                            {sku.sku_name || sku.sku_code || `SKU ${sku.sku_id}`}
                                                                        </span>
                                                                    ))
                                                                ) : cfg.configuration_name ? (
                                                                    <span style={{ color: '#6C757D', fontStyle: 'italic', fontSize: '11px' }}>No SKUs assigned</span>
                                                                ) : (
                                                                    <span style={{ color: '#ADB5BD' }}>NA</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="empty-state">
                                                    <div className="empty-state-content">
                                                        <div className="icon">
                                                            <img src={noBrandImage} alt="avatar" className="avatar" style={{ width: 120, height: 120 }} />
                                                        </div>
                                                        <h1 className="no-item-title mt-2">No Related Configurations</h1>
                                                        <p className="no-item-text">No Configurations defined under this SPU</p>
                                                    </div>
                                                </div>
                                            )}
                                            {totalSkuConfigs > SKU_CONFIGS_PER_PAGE && (
                                                <div className="pagination" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <div className="pagination-info" style={{ textAlign: 'right', paddingTop: '10px', fontSize: '12px', color: '#6C757D', paddingRight: '10px', paddingBottom: '10px' }}>
                                                        {`${configStartIndex + 1} - ${configEndIndex} of ${totalSkuConfigs}`}
                                                    </div>
                                                    <Pagination currentPage={skuConfigPage} totalPages={totalSkuConfigPages} onPageChange={setSkuConfigPage} />
                                                </div>
                                            )}
                                            {totalSkuConfigs > 0 && totalSkuConfigs <= SKU_CONFIGS_PER_PAGE && (
                                                <div style={{ textAlign: 'right', paddingTop: '10px', fontSize: '12px', color: '#6C757D', paddingRight: '20px', paddingBottom: '10px' }}>
                                                    1 - {totalSkuConfigs} of {totalSkuConfigs}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* ── CSS (same as spusku.js) ── */}
            <style>{`
                .cell-wrapper {
                    width: 100%;
                    word-break: break-all;
                    cursor: text;
                }
                .value-container {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .value-clickable {
                    color: #2C7BE5;
                    text-decoration: underline;
                    cursor: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Ctext y='24' font-size='24'%3E%E2%9C%8B%3C/text%3E%3C/svg%3E") 12 12, pointer;
                }
                .copy-icon {
                    cursor: url("data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32'%3E%3Ctext y='24' font-size='24'%3E%E2%9C%8B%3C/text%3E%3C/svg%3E") 12 12, pointer;
                }
                .copied-label {
                    font-size: 12px;
                    color: #2C7BE5;
                }
            `}</style>
        </>
    );
};

export default SpuInfoDrawer;
